import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-input',
  templateUrl: './app-input.component.html',
  styleUrls: ['./app-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppInputComponent),
      multi: true,
    },
  ],
})
export class AppInputComponent implements ControlValueAccessor {
  @Input() labelText: string = '';
  @Input() label: string = '';
  @Input() mandatory: boolean = false;
  @Input() required: boolean = false;
  @Input() optional: boolean = false;
  @Input() tooltip: string = '';
  @Input() labelIcon: string = '';
  @Input() labelSize: 'sm' | 'md' | 'lg' = 'md';

  get resolvedLabel(): string {
    return this.labelText || this.label || '';
  }

  get isRequired(): boolean {
    return this.mandatory || this.required;
  }

  @Input() type: string = 'text';
  @Input() placeholder: string = '';
  @Input() name: string = '';
  @Input() id: string = '';

  private _disabled: boolean = false;
  private _inputDisabled: boolean = false;
  @Input()
  set disabled(val: any) {
    this._inputDisabled = val === '' || val === true || val === 'true';
    this._disabled = this._inputDisabled;
  }
  get disabled(): boolean {
    return this._disabled;
  }

  @Input() readonly: boolean = false;
  @Input() errorMsg: string | null = null;
  @Input() helperText: string = '';

  @Input() prefixIcon: string = '';
  @Input() suffixIcon: string = '';
  @Input() prefixText: string = '';
  @Input() suffixText: string = '';
  @Input() clearable: boolean = false;

  @Input() multiline: boolean = false;
  @Input() rows: number = 3;

  @Input() allowDecimal: boolean = false;
  @Input() decimalPlaces: number = 2;
  @Input() onlyNumbers: boolean = false;

  @Input() size: 'sm' | 'md' | 'lg' | 'compact' = 'md';
  @Input() height: number | string = '';
  @Input() min: number | string = '';
  @Input() max: number | string = '';
  @Input() step: number | string = '';
  @Input() maxlength: number | string = '';
  @Input() autocomplete: string = 'off';

  @Input() customClass: string = '';
  @Input() inputClass: string = '';
  @Input() containerClass: string = '';

  @Output() inputChange = new EventEmitter<any>();
  @Output() inputBlur = new EventEmitter<FocusEvent>();
  @Output() inputFocus = new EventEmitter<FocusEvent>();
  @Output() inputKeydown = new EventEmitter<KeyboardEvent>();
  @Output() inputKeyup = new EventEmitter<KeyboardEvent>();
  @Output() clearClick = new EventEmitter<void>();

  @ViewChild('inputRef') inputRef!: ElementRef<HTMLInputElement | HTMLTextAreaElement>;

  value: any = '';
  isFocused: boolean = false;
  isPasswordVisible: boolean = false;

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private cdr: ChangeDetectorRef) {}

  writeValue(val: any): void {
    let sanitized = val !== undefined && val !== null ? String(val) : '';
    if (this.onlyNumbers) {
      sanitized = sanitized.replace(/\D/g, '');
    } else if (this.allowDecimal && sanitized) {
      const regex = new RegExp(`^\\d*(\\.\\d{0,${this.decimalPlaces}})?`);
      const match = sanitized.match(regex);
      sanitized = match ? match[0] : '';
    }
    this.value = sanitized;
    if (this.inputRef?.nativeElement) {
      this.inputRef.nativeElement.value = sanitized;
    }
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled = this._inputDisabled || isDisabled;
    this.cdr.markForCheck();
  }

  onModelChange(event: any): void {
    const inputEl = event?.target as HTMLInputElement;
    let val = inputEl ? inputEl.value : event;
    val = val !== undefined && val !== null ? String(val) : '';

    if (this.onlyNumbers) {
      val = val.replace(/\D/g, '');
      if (inputEl) {
        inputEl.value = val;
      }
    } else if (this.allowDecimal) {
      const regex = new RegExp(`^\\d*(\\.\\d{0,${this.decimalPlaces}})?`);
      const match = val.match(regex);
      val = match ? match[0] : '';
      if (inputEl) {
        inputEl.value = val;
      }
    }

    this.value = val;
    this.onChange(val);
    this.inputChange.emit(val);
  }

  onBlur(event: FocusEvent): void {
    this.isFocused = false;
    this.onTouched();
    this.inputBlur.emit(event);
  }

  onFocus(event: FocusEvent): void {
    this.isFocused = true;
    this.inputFocus.emit(event);
  }

  onKeyDown(event: KeyboardEvent): void {
    if (this.onlyNumbers || this.allowDecimal) {
      const controlKeys = ['Backspace', 'Tab', 'Enter', 'Escape', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
      if (controlKeys.includes(event.key) || event.ctrlKey || event.metaKey) {
        this.inputKeydown.emit(event);
        return;
      }

      if (this.onlyNumbers) {
        if (!/^\d$/.test(event.key)) {
          event.preventDefault();
          return;
        }
      } else if (this.allowDecimal) {
        const inputEl = event.target as HTMLInputElement;
        const currentVal = inputEl?.value || '';

        if (event.key === '.') {
          if (this.decimalPlaces <= 0 || currentVal.includes('.')) {
            event.preventDefault();
            return;
          }
        } else if (!/^\d$/.test(event.key)) {
          event.preventDefault();
          return;
        }
      }
    }
    this.inputKeydown.emit(event);
  }

  onKeyUp(event: KeyboardEvent): void {
    this.inputKeyup.emit(event);
  }

  handleClear(): void {
    this.value = '';
    this.onChange('');
    this.inputChange.emit('');
    this.clearClick.emit();
    if (this.inputRef?.nativeElement) {
      this.inputRef.nativeElement.focus();
    }
  }

  togglePasswordVisibility(): void {
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  get effectiveType(): string {
    if (this.type === 'password') {
      return this.isPasswordVisible ? 'text' : 'password';
    }
    return this.type;
  }

  get hasError(): boolean {
    return !!this.errorMsg && this.errorMsg.trim().length > 0;
  }

  get resolvedHeight(): string | null {
    if (this.height) {
      return typeof this.height === 'number' ? `${this.height}px` : this.height;
    }
    return null;
  }
}
